import { supabase } from '@/integrations/supabase/client';
import type { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import {
  directPathError,
  getStaffSessionToken,
  handleInvalidSession,
  isInvalidSessionError,
} from '@/services/staffSession';

/**
 * Referral counts per dentist, from patient_cases.
 *
 * patient_cases is no longer reachable from the browser with the publishable
 * key (migration 20260930120000), so the counts come from a session-bound
 * SECURITY DEFINER function. It returns counts and dates only — the old code
 * pulled one row per case, over a thousand of them, purely to call .length on
 * the result.
 *
 * The direct read stays as a fallback for a browser with no session token (an
 * older build, or a database without the migrations), and stops working by
 * itself once Phase 3 is applied.
 */
interface ReferralMetricRow {
  referring_doctor_id: string;
  total_referrals: number;
  last_referral_date: string | null;
}

const fetchReferralMetricsBySession = async (
  sessionToken: string,
  doctorIds: string[] | null,
): Promise<ReferralMetricRow[]> => {
  const { data, error } = await (supabase.rpc as unknown as (
    fn: string,
    args: Record<string, unknown>,
  ) => Promise<{ data: ReferralMetricRow[] | null; error: { code?: string; message: string } | null }>)(
    'get_referral_metrics_by_session',
    { p_session_token: sessionToken, p_doctor_ids: doctorIds },
  );

  if (error) {
    if (isInvalidSessionError(error)) {
      handleInvalidSession();
    }
    throw error;
  }

  return data ?? [];
};

/**
 * The portal-account functions (create login, reset password, delete dentist)
 * were callable by anyone holding the publishable key: account takeover. With a
 * staff token the dashboard now goes through *_by_session wrappers
 * (20260930110000); the originals are revoked from the public key in
 * 20260930120000. The direct call stays only for a browser with no token.
 */
const callSessionRpc = async <T>(fn: string, args: Record<string, unknown>) =>
  await (supabase.rpc as unknown as (
    name: string,
    params: Record<string, unknown>,
  ) => Promise<{ data: T | null; error: { code?: string; message: string } | null }>)(fn, args);

/** A rejected token or a refused public key signs the user out, then rethrows. */
const sessionAwareError = <E>(error: E): E => {
  if (isInvalidSessionError(error)) {
    handleInvalidSession();
    return error;
  }
  return directPathError(error);
};

export type ReferringClinic = Tables<'referring_clinics'>;
export type ReferringClinicInsert = TablesInsert<'referring_clinics'>;
export type ReferringClinicUpdate = TablesUpdate<'referring_clinics'>;

export type ReferringDoctor = Tables<'referring_doctors'>;
export type ReferringDoctorInsert = TablesInsert<'referring_doctors'>;
export type ReferringDoctorUpdate = TablesUpdate<'referring_doctors'>;

export type ExternalReferralAccount = Tables<'external_referral_accounts'>;

export interface ReferringDoctorWithStats extends ReferringDoctor {
  clinic: ReferringClinic | null;
  portal_account: Pick<
    ExternalReferralAccount,
    'id' | 'email' | 'is_active' | 'last_login_at' | 'role' | 'password_reset_required'
  > | null;
  total_referrals: number;
  last_referral_date: string | null;
  /**
   * Count of patients whose Plato "Referred By" resolves to this dentist,
   * sourced from the Plato mapping (plato_patient_referrers -> plato_referral_contacts),
   * NOT from patient_cases. This is the real referral volume for Plato-sourced
   * dentists, who have few/no portal cases. See get_referrer_patient_counts().
   */
  plato_patient_count: number;
}

/** One patient a referring dentist sent (from the Plato mapping). */
export interface ReferredPatientRow {
  patient_name: string | null;
  plato_patient_id: string;
  status: string | null;
  last_seen: string | null;
  /** ongoing | discharge_drafted | discharged — from the Plato /letter signal. */
  treatment_status: string | null;
  /** Latest derived endodontic stage (stage_1 / stage_2 / review / …). */
  current_stage: string | null;
}

/** One row of a patient's derived treatment stage timeline. */
export interface PatientStageRow {
  stage_date: string | null;
  stage: string;
  description: string | null;
}

/** One row of the global referral case list (every mapped patient). */
export interface ReferralCaselistRow {
  plato_patient_id: string;
  patient_name: string | null;
  clinic_name: string | null;
  dentist_name: string | null;
  referring_doctor_id: string | null;
  treatment_status: string | null;
  current_stage: string | null;
  last_seen: string | null;
}

export interface ListReferringDoctorsFilters {
  search?: string;
  clinicId?: string | 'all';
  activeStatus?: 'all' | 'active' | 'inactive';
  hasPortalAccount?: 'all' | 'yes' | 'no';
}

export interface CreateReferringDoctorInput {
  name: string;
  clinic_id?: string | null;
  email?: string | null;
  phone?: string | null;
  specialty?: string | null;
  /** Singapore Dental Council registration — 5 digits + 1 letter (e.g. 31432I). Stored uppercase. */
  dcr_number?: string | null;
  is_twin_city_doctor?: boolean;
  is_active?: boolean;
}

export type UpdateReferringDoctorInput = Partial<CreateReferringDoctorInput>;

export interface CreateReferringClinicInput {
  name: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  notes?: string | null;
  is_active?: boolean;
}

/**
 * Fetch every referring doctor with joined clinic, optional portal account,
 * and referral-volume metrics derived from patient_cases.
 *
 * Filters are applied to the doctor table first (server-side) for `search`,
 * `clinicId`, and `activeStatus`. `hasPortalAccount` filters on the joined
 * portal-account column client-side because Supabase doesn't expose
 * `null` filtering through nested-resource selects cleanly.
 */
export async function listReferringDoctors(
  filters: ListReferringDoctorsFilters = {},
): Promise<ReferringDoctorWithStats[]> {
  let doctorsQuery = supabase
    .from('referring_doctors')
    .select('*')
    .order('name', { ascending: true });

  if (filters.search && filters.search.trim() !== '') {
    const term = `%${filters.search.trim()}%`;
    doctorsQuery = doctorsQuery.or(
      `name.ilike.${term},email.ilike.${term},phone.ilike.${term},specialty.ilike.${term}`,
    );
  }

  if (filters.clinicId && filters.clinicId !== 'all') {
    doctorsQuery = doctorsQuery.eq('clinic_id', filters.clinicId);
  }

  if (filters.activeStatus === 'active') {
    doctorsQuery = doctorsQuery.eq('is_active', true);
  } else if (filters.activeStatus === 'inactive') {
    doctorsQuery = doctorsQuery.eq('is_active', false);
  }

  const { data: doctors, error } = await doctorsQuery;
  if (error) {
    console.error('listReferringDoctors error:', error);
    throw error;
  }

  const doctorRows = (doctors ?? []) as ReferringDoctor[];
  const doctorIds = doctorRows.map((d) => d.id);

  // Fetch clinics for every doctor that has a clinic_id assigned.
  const clinicIds = Array.from(
    new Set(doctorRows.map((d) => d.clinic_id).filter((v): v is string => !!v)),
  );
  const clinicMap = new Map<string, ReferringClinic>();
  if (clinicIds.length > 0) {
    const { data: clinicRows, error: clinicError } = await supabase
      .from('referring_clinics')
      .select('*')
      .in('id', clinicIds);
    if (clinicError) {
      console.warn('listReferringDoctors: clinic lookup failed.', clinicError);
    } else {
      (clinicRows ?? []).forEach((row) => {
        clinicMap.set(row.id, row as ReferringClinic);
      });
    }
  }

  // Fetch portal accounts for every doctor in one query, keyed by doctor id.
  const portalMap = new Map<string, ReferringDoctorWithStats['portal_account']>();
  if (doctorIds.length > 0) {
    const { data: accountRows, error: accountError } = await supabase
      .from('external_referral_accounts')
      .select('id, email, is_active, last_login_at, role, password_reset_required, referring_doctor_id')
      .in('referring_doctor_id', doctorIds);
    if (accountError) {
      console.warn('listReferringDoctors: portal-account lookup failed.', accountError);
    } else {
      (accountRows ?? []).forEach((row) => {
        if (!row.referring_doctor_id) return;
        // Keep the first account per doctor — multiple accounts per doctor is rare.
        if (!portalMap.has(row.referring_doctor_id)) {
          portalMap.set(row.referring_doctor_id, {
            id: row.id,
            email: row.email,
            is_active: row.is_active,
            last_login_at: row.last_login_at,
            role: row.role,
            password_reset_required: row.password_reset_required,
          });
        }
      });
    }
  }

  // Build referral metrics from patient_cases in a separate query
  // (we only need counts + the most recent referral_received_date).
  const referralMetrics = new Map<
    string,
    { total: number; lastDate: string | null }
  >();

  const metricsSessionToken = getStaffSessionToken();

  if (doctorIds.length > 0 && metricsSessionToken) {
    try {
      const metrics = await fetchReferralMetricsBySession(metricsSessionToken, doctorIds);
      metrics.forEach((row) => {
        referralMetrics.set(row.referring_doctor_id, {
          total: Number(row.total_referrals) || 0,
          lastDate: row.last_referral_date ?? null,
        });
      });
    } catch (metricsError) {
      // Same degradation as before: the directory renders with zero counts
      // rather than not rendering at all.
      console.warn(
        'listReferringDoctors: could not load referral metrics; falling back to zeros.',
        metricsError,
      );
    }
  } else if (doctorIds.length > 0) {
    // Page through: the case table exceeds PostgREST's 1000-row cap after the
    // Plato backfill, and an unpaginated read would undercount every dentist's
    // referral total on the directory.
    const PAGE = 1000;
    const cases: { referring_doctor_id: string | null; referral_received_date: string | null }[] = [];
    let casesError: unknown = null;
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await supabase
        .from('patient_cases')
        .select('referring_doctor_id, referral_received_date')
        .in('referring_doctor_id', doctorIds)
        .range(from, from + PAGE - 1);

      if (error) {
        casesError = error;
        break;
      }

      const page = data ?? [];
      cases.push(...page);
      if (page.length < PAGE) break;
    }

    if (casesError) {
      console.warn(
        'listReferringDoctors: could not load referral metrics; falling back to zeros.',
        casesError,
      );
    } else {
      cases.forEach((row) => {
        const docId = row.referring_doctor_id;
        if (!docId) return;
        const existing = referralMetrics.get(docId) ?? {
          total: 0,
          lastDate: null,
        };
        existing.total += 1;
        const candidate = row.referral_received_date ?? null;
        if (candidate && (!existing.lastDate || candidate > existing.lastDate)) {
          existing.lastDate = candidate;
        }
        referralMetrics.set(docId, existing);
      });
    }
  }

  // Plato-sourced patient counts per referring doctor (the real volume for
  // dentists imported from Plato, who have few/no portal patient_cases).
  const platoCounts = new Map<string, number>();
  {
    const { data: counts, error: countsError } = await (supabase.rpc as any)(
      'get_referrer_patient_counts',
    );
    if (countsError) {
      console.warn('listReferringDoctors: Plato referrer counts unavailable.', countsError);
    } else {
      (counts ?? []).forEach((row: { referring_doctor_id: string | null; plato_patient_count: number }) => {
        if (row.referring_doctor_id) {
          platoCounts.set(row.referring_doctor_id, Number(row.plato_patient_count) || 0);
        }
      });
    }
  }

  const enriched: ReferringDoctorWithStats[] = doctorRows.map((row) => {
    const metrics = referralMetrics.get(row.id) ?? {
      total: 0,
      lastDate: null,
    };
    return {
      ...row,
      clinic: row.clinic_id ? clinicMap.get(row.clinic_id) ?? null : null,
      portal_account: portalMap.get(row.id) ?? null,
      total_referrals: metrics.total,
      last_referral_date: metrics.lastDate,
      plato_patient_count: platoCounts.get(row.id) ?? 0,
    };
  });

  if (filters.hasPortalAccount === 'yes') {
    return enriched.filter((d) => d.portal_account !== null);
  }
  if (filters.hasPortalAccount === 'no') {
    return enriched.filter((d) => d.portal_account === null);
  }

  return enriched;
}

export async function getReferringDoctor(id: string): Promise<ReferringDoctorWithStats | null> {
  const { data, error } = await supabase
    .from('referring_doctors')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error('getReferringDoctor error:', error);
    throw error;
  }
  if (!data) return null;
  const doctor = data as ReferringDoctor;

  let clinic: ReferringClinic | null = null;
  if (doctor.clinic_id) {
    const { data: clinicRow } = await supabase
      .from('referring_clinics')
      .select('*')
      .eq('id', doctor.clinic_id)
      .maybeSingle();
    clinic = (clinicRow as ReferringClinic | null) ?? null;
  }

  const { data: portalRows } = await supabase
    .from('external_referral_accounts')
    .select('id, email, is_active, last_login_at, role, password_reset_required')
    .eq('referring_doctor_id', id)
    .limit(1);
  const portalAccount = portalRows && portalRows.length > 0 ? portalRows[0] : null;

  const sessionToken = getStaffSessionToken();

  let totalReferrals = 0;
  let lastReferralDate: string | null = null;

  if (sessionToken) {
    const [metrics] = await fetchReferralMetricsBySession(sessionToken, [id]);
    totalReferrals = Number(metrics?.total_referrals) || 0;
    lastReferralDate = metrics?.last_referral_date ?? null;
  } else {
    const { data: cases } = await supabase
      .from('patient_cases')
      .select('referral_received_date')
      .eq('referring_doctor_id', id);

    totalReferrals = cases?.length ?? 0;
    lastReferralDate =
      cases?.reduce<string | null>((acc, row) => {
        const candidate = row.referral_received_date ?? null;
        if (candidate && (!acc || candidate > acc)) return candidate;
        return acc;
      }, null) ?? null;
  }

  return {
    ...doctor,
    clinic,
    portal_account: portalAccount,
    total_referrals: totalReferrals,
    last_referral_date: lastReferralDate,
    plato_patient_count: 0,
  };
}

/**
 * The patients a referring dentist sent, from the Plato "Referred By" mapping,
 * each with best-available status (portal case status, else latest Plato visit).
 * Backs the click-through modal on the Referring Dentists table.
 */
export async function getPatientsByReferringDoctor(
  doctorId: string,
): Promise<ReferredPatientRow[]> {
  const { data, error } = await (supabase.rpc as any)('get_patients_by_referring_doctor', {
    p_doctor_id: doctorId,
  });
  if (error) {
    console.error('getPatientsByReferringDoctor error:', error);
    throw error;
  }
  return (data ?? []) as ReferredPatientRow[];
}

/** Every referred patient in one flat list, with treatment status + latest stage. */
export async function getReferralCaselist(): Promise<ReferralCaselistRow[]> {
  const { data, error } = await (supabase.rpc as any)('get_referral_caselist');
  if (error) {
    console.error('getReferralCaselist error:', error);
    throw error;
  }
  return (data ?? []) as ReferralCaselistRow[];
}

/** The derived endodontic stage timeline for one patient (internal detail view). */
export async function getPatientStageTimeline(
  platoPatientId: string,
): Promise<PatientStageRow[]> {
  const { data, error } = await (supabase.rpc as any)('get_patient_stage_timeline', {
    p_plato_patient_id: platoPatientId,
  });
  if (error) {
    console.error('getPatientStageTimeline error:', error);
    throw error;
  }
  return (data ?? []) as PatientStageRow[];
}

function normaliseInput(
  input: CreateReferringDoctorInput | UpdateReferringDoctorInput,
): ReferringDoctorInsert {
  return {
    name: (input.name ?? '').trim(),
    clinic_id: input.clinic_id ?? null,
    email: input.email && input.email.trim() !== '' ? input.email.trim().toLowerCase() : null,
    phone: input.phone && input.phone.trim() !== '' ? input.phone.trim() : null,
    specialty: input.specialty && input.specialty.trim() !== '' ? input.specialty.trim() : null,
    // dcr_number column added in migration 20260527100000; generated types may
    // predate it, hence the cast on the return below.
    dcr_number:
      input.dcr_number && input.dcr_number.trim() !== ''
        ? input.dcr_number.trim().toUpperCase()
        : null,
    is_twin_city_doctor: input.is_twin_city_doctor ?? false,
    is_active: input.is_active ?? true,
  } as ReferringDoctorInsert;
}

/**
 * Find another dentist already holding the given DCR number (case-insensitive).
 * Used as a friendly pre-save duplicate check — the DB unique index on
 * upper(dcr_number) remains the hard backstop.
 */
export async function findReferringDoctorByDcr(
  dcrNumber: string,
  excludeDoctorId?: string,
): Promise<Pick<ReferringDoctor, 'id' | 'name'> | null> {
  const normalized = dcrNumber.trim().toUpperCase();
  if (normalized === '') return null;

  let query = supabase
    .from('referring_doctors')
    .select('id, name')
    .ilike('dcr_number' as any, normalized)
    .limit(1);

  if (excludeDoctorId) {
    query = query.neq('id', excludeDoctorId);
  }

  const { data, error } = await query;
  if (error) {
    console.error('findReferringDoctorByDcr error:', error);
    throw error;
  }
  return (data?.[0] as Pick<ReferringDoctor, 'id' | 'name'> | undefined) ?? null;
}

export async function createReferringDoctor(
  input: CreateReferringDoctorInput,
): Promise<ReferringDoctor> {
  if (!input.name || input.name.trim() === '') {
    throw new Error('Doctor name is required.');
  }
  const payload = normaliseInput(input);
  const { data, error } = await supabase
    .from('referring_doctors')
    .insert(payload)
    .select('*')
    .single();

  if (error) {
    console.error('createReferringDoctor error:', error);
    throw error;
  }
  return data as ReferringDoctor;
}

export async function updateReferringDoctor(
  id: string,
  input: UpdateReferringDoctorInput,
): Promise<ReferringDoctor> {
  const payload: ReferringDoctorUpdate = {};
  if (input.name !== undefined) payload.name = input.name.trim();
  if (input.clinic_id !== undefined) payload.clinic_id = input.clinic_id;
  if (input.email !== undefined) {
    payload.email =
      input.email && input.email.trim() !== '' ? input.email.trim().toLowerCase() : null;
  }
  if (input.phone !== undefined) {
    payload.phone = input.phone && input.phone.trim() !== '' ? input.phone.trim() : null;
  }
  if (input.specialty !== undefined) {
    payload.specialty =
      input.specialty && input.specialty.trim() !== '' ? input.specialty.trim() : null;
  }
  if (input.dcr_number !== undefined) {
    // dcr_number column added in migration 20260527100000; generated types may
    // predate it, hence the record cast.
    (payload as Record<string, unknown>).dcr_number =
      input.dcr_number && input.dcr_number.trim() !== ''
        ? input.dcr_number.trim().toUpperCase()
        : null;
  }
  if (input.is_twin_city_doctor !== undefined) {
    payload.is_twin_city_doctor = input.is_twin_city_doctor;
  }
  if (input.is_active !== undefined) {
    payload.is_active = input.is_active;
  }
  payload.updated_at = new Date().toISOString();

  const { data, error } = await supabase
    .from('referring_doctors')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    console.error('updateReferringDoctor error:', error);
    throw error;
  }
  return data as ReferringDoctor;
}

export async function toggleReferringDoctorActive(
  id: string,
  active: boolean,
): Promise<ReferringDoctor> {
  return updateReferringDoctor(id, { is_active: active });
}

/**
 * Create a referring-dentist portal account for an existing directory doctor.
 * Links the account to the doctor (and their clinic) so the portal shows that
 * dentist's referrals. Returns the new external-account id.
 */
export async function createReferringPortalAccount(params: {
  email: string;
  name: string;
  password: string;
  doctorId: string;
  clinicId: string | null;
}): Promise<string> {
  if (!params.email.trim()) {
    throw new Error('An email is required to create the portal login.');
  }
  if (!params.password.trim()) {
    throw new Error('A temporary password is required.');
  }

  const sessionToken = getStaffSessionToken();
  const { data, error } = sessionToken
    ? await callSessionRpc<string>('create_external_referral_account_by_session', {
        p_session_token: sessionToken,
        p_email: params.email.trim().toLowerCase(),
        p_name: params.name,
        p_password: params.password,
        p_role: 'referring_dentist',
        p_doctor_id: params.doctorId,
        p_clinic_id: params.clinicId,
      })
    : await supabase.rpc('create_external_referral_account', {
        account_email: params.email.trim().toLowerCase(),
        account_name: params.name,
        account_password: params.password,
        account_role: 'referring_dentist',
        doctor_id: params.doctorId,
        clinic_id: params.clinicId,
      });

  if (error) {
    console.error('createReferringPortalAccount error:', error);
    throw sessionAwareError(error);
  }
  return data as string;
}

/** Reset a referring dentist's portal password (they'll be asked to change it). */
export async function resetReferringPortalPassword(
  accountId: string,
  newPassword: string,
): Promise<void> {
  const sessionToken = getStaffSessionToken();
  const { error } = sessionToken
    ? await callSessionRpc<boolean>('reset_external_referral_password_by_session', {
        p_session_token: sessionToken,
        p_account_id: accountId,
        p_new_password: newPassword,
      })
    : await supabase.rpc('reset_external_referral_password', {
        p_account_id: accountId,
        p_new_password: newPassword,
      });
  if (error) {
    console.error('resetReferringPortalPassword error:', error);
    throw sessionAwareError(error);
  }
}

/** Delete a referring dentist + their linked portal account. */
export async function deleteReferringDoctor(doctorId: string): Promise<void> {
  const sessionToken = getStaffSessionToken();
  const { error } = sessionToken
    ? await callSessionRpc<boolean>('delete_referring_doctor_by_session', {
        p_session_token: sessionToken,
        p_doctor_id: doctorId,
      })
    : await supabase.rpc('delete_referring_doctor', {
        p_doctor_id: doctorId,
      });
  if (error) {
    console.error('deleteReferringDoctor error:', error);
    throw sessionAwareError(error);
  }
}

export async function listReferringClinics(
  options: { activeOnly?: boolean } = {},
): Promise<ReferringClinic[]> {
  let query = supabase
    .from('referring_clinics')
    .select('*')
    .order('name', { ascending: true });

  if (options.activeOnly) {
    query = query.eq('is_active', true);
  }

  const { data, error } = await query;
  if (error) {
    console.error('listReferringClinics error:', error);
    throw error;
  }
  return (data ?? []) as ReferringClinic[];
}

export async function createReferringClinic(
  input: CreateReferringClinicInput,
): Promise<ReferringClinic> {
  if (!input.name || input.name.trim() === '') {
    throw new Error('Clinic name is required.');
  }
  const payload: ReferringClinicInsert = {
    name: input.name.trim(),
    email: input.email && input.email.trim() !== '' ? input.email.trim().toLowerCase() : null,
    phone: input.phone && input.phone.trim() !== '' ? input.phone.trim() : null,
    address: input.address && input.address.trim() !== '' ? input.address.trim() : null,
    notes: input.notes && input.notes.trim() !== '' ? input.notes.trim() : null,
    is_active: input.is_active ?? true,
  };

  const { data, error } = await supabase
    .from('referring_clinics')
    .insert(payload)
    .select('*')
    .single();

  if (error) {
    console.error('createReferringClinic error:', error);
    throw error;
  }
  return data as ReferringClinic;
}
